/**
 * Деньги в проекте — целое число копеек (Postgres `integer`).
 *
 * Потолок int4 — 2 147 483 647 копеек (21 474 836,47 ₽) на одно значение, что
 * на шесть порядков ниже Number.MAX_SAFE_INTEGER, поэтому арифметика в JS
 * точна и без bigint. Два места требуют внимания, и оба закрыты здесь:
 *
 *  1. `SUM()` над `integer` в Postgres возвращает `bigint`, а node-postgres
 *     отдаёт bigint строкой — см. {@link parseSum}.
 *  2. Деление (расчёт цены токенов) — единственный источник дробей, и оно
 *     обязано округляться явно — см. {@link divideRound}.
 */
/** Максимум, влезающий в Postgres `integer`. */
export const INT4_MAX = 2_147_483_647;
/** Минимум, влезающий в Postgres `integer`. */
export const INT4_MIN = -2_147_483_648;
export class MoneyOverflowError extends Error {
    constructor(value) {
        super(`Денежная величина ${value} не помещается в integer копеек`);
        this.name = 'MoneyOverflowError';
    }
}
/** Проверяет, что величина — целое число копеек, влезающее в int4. */
export function assertKopecks(value) {
    if (!Number.isInteger(value)) {
        throw new MoneyOverflowError(`${value} (не целое)`);
    }
    if (value > INT4_MAX || value < INT4_MIN) {
        throw new MoneyOverflowError(value);
    }
    return value;
}
/**
 * Разбирает результат SQL-агрегата (`SUM`, `COALESCE(SUM(...), 0)`).
 *
 * node-postgres возвращает `bigint` строкой, чтобы не потерять точность молча.
 * Здесь мы это разворачиваем явно и проверяем, что результат остался в
 * безопасном для JS диапазоне — иначе получили бы тихую потерю точности.
 */
export function parseSum(value) {
    if (value === null || value === undefined)
        return 0;
    if (typeof value === 'number') {
        if (!Number.isSafeInteger(value))
            throw new MoneyOverflowError(value);
        return value;
    }
    const parsed = typeof value === 'bigint' ? value : BigInt(value);
    if (parsed > BigInt(Number.MAX_SAFE_INTEGER) || parsed < BigInt(Number.MIN_SAFE_INTEGER)) {
        throw new MoneyOverflowError(parsed.toString());
    }
    return Number(parsed);
}
/**
 * Деление с округлением half-up по модулю (симметрично относительно нуля):
 * 0.5 → 1, −0.5 → −1.
 *
 * Math.round() не подходит: он округляет −0.5 к нулю, из-за чего списание и
 * возврат одной и той же величины давали бы разные суммы.
 */
export function divideRound(numerator, denominator) {
    if (denominator === 0)
        throw new Error('Деление на ноль при расчёте суммы');
    const sign = Math.sign(numerator) * Math.sign(denominator) || 1;
    return sign * Math.round(Math.abs(numerator) / Math.abs(denominator));
}
/** Форматирует копейки как «1 234,56» (без символа валюты). */
export function formatKopecks(kopecks) {
    const sign = kopecks < 0 ? '-' : '';
    const abs = Math.abs(kopecks);
    const rubles = Math.trunc(abs / 100);
    const rest = abs % 100;
    const grouped = rubles.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${sign}${grouped},${rest.toString().padStart(2, '0')}`;
}
/** Форматирует копейки как «1 234,56 ₽». */
export function formatRubles(kopecks) {
    return `${formatKopecks(kopecks)} ₽`;
}
/**
 * Цена для витрины: копейки показываются, только если они есть.
 *
 * «4 990 ₽» вместо «4 990,00 ₽». Два нуля после запятой в ценнике не несут
 * ничего, кроме шума. В счёте, проводке и остатке баланса они, наоборот,
 * обязательны: там сумма точная и её сверяют с выпиской. Поэтому формат
 * отдельный, а не поведение общего — перепутать их значит однажды округлить
 * сумму в документе.
 */
export function formatPrice(kopecks) {
    return kopecks % 100 === 0
        ? `${formatKopecks(kopecks).replace(/,00$/, '')} ₽`
        : formatRubles(kopecks);
}
//# sourceMappingURL=money.js.map